import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PieChartSlices } from '~/modules/artist-roster/components/PieChart/PieChart';
import { PieChart } from '~/modules/artist-roster/components/PieChart/PieChart';
import { formatCurrency } from '~/modules/artist-roster/pages/artist-details/pages/artist-benchmarks/lib/utils';
import type {
  ActiveType,
  Markets,
  Platforms,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/types';
import type { Platform } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/types';
import {
  getMarketSliceColor,
  getPlatformSliceColor,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlan/components/BudgetDistribution/utils/getSliceColor';

export interface BudgetPieChartProps {
  activeType: ActiveType;
  totalBudget: number;
  data: Markets | Platforms;
  hoveredIndex: number | null;
  setHoveredIndex: (index: number | null) => void;
}

export function BudgetPieChart({
  activeType,
  totalBudget,
  data,
  hoveredIndex,
  setHoveredIndex,
}: BudgetPieChartProps) {
  const { t } = useTranslation('campaign-planner');

  const slices: PieChartSlices = useMemo(
    () =>
      data.map((item, index) => ({
        name: item.name,
        color:
          activeType === 'platforms'
            ? getPlatformSliceColor((item as Platform).name)
            : getMarketSliceColor(index),
        percent: item.budget / totalBudget,
      })),
    [activeType, data, totalBudget],
  );

  return (
    <div
      className="mr-xl grid h-[200px] shrink-0 basis-[200px]"
      aria-label={t(
        'digitalCampaigns.mediaPlan.budgetDistribution.pieChart.label',
      )}
    >
      <div className="col-start-1 row-start-1 grid place-items-center">
        <div className="grid place-items-center">
          <div className="text-body-sm leading-tight">
            {t(
              'digitalCampaigns.mediaPlan.budgetDistribution.pieChart.totalBudget',
            )}
          </div>
          <div className="text-headline-md font-bold leading-none">
            {formatCurrency({
              value: totalBudget,
              minimumDecimals: 0,
            })}
          </div>
        </div>
      </div>
      <div className="relative z-10 col-start-1 row-start-1">
        <PieChart
          slices={slices}
          hoveredIndex={hoveredIndex}
          onSliceHover={setHoveredIndex}
        />
      </div>
    </div>
  );
}

BudgetPieChart.displayName = 'BudgetPieChart';
